export type TemplateType = 'base' | 'startup' | 'campaign' | 'content' | 'product';

export interface TemplateDefinition {
  key: TemplateType;
  label: string;
  description: string;
}

export interface StructureField {
  key: string;
  label: string;
  placeholder?: string;
}

export const TEMPLATES: TemplateDefinition[] = [
  { key: 'base', label: 'Blank', description: 'General idea structure' },
  { key: 'startup', label: 'Startup', description: 'Business idea structure' },
  { key: 'campaign', label: 'Campaign', description: 'Marketing campaign plan' },
  { key: 'content', label: 'Content', description: 'Content creation brief' },
  { key: 'product', label: 'Product', description: 'Product development brief' },
];

export const STRUCTURE_SCHEMAS: Record<TemplateType, StructureField[]> = {
  base: [
    { key: 'one_liner', label: 'One-liner', placeholder: 'Summarize in one sentence...' },
    { key: 'why_it_matters', label: 'Why it matters', placeholder: 'Why is this important?' },
    { key: 'who_its_for', label: 'Who it\'s for', placeholder: 'Who benefits from this?' },
    { key: 'core_mechanism', label: 'Core mechanism', placeholder: 'How does it work?' },
    { key: 'constraints', label: 'Constraints / assumptions', placeholder: 'What are the limits or assumptions?' },
    { key: 'risks_unknowns', label: 'Risks / unknowns', placeholder: 'What could go wrong or is uncertain?' },
    { key: 'next_action', label: 'Next action', placeholder: 'What\'s the very next step?' },
  ],
  startup: [
    { key: 'one_liner', label: 'One-liner', placeholder: 'Summarize in one sentence...' },
    { key: 'problem', label: 'Problem', placeholder: 'What problem does this solve?' },
    { key: 'audience', label: 'Audience', placeholder: 'Who is this for?' },
    { key: 'proposed_solution', label: 'Proposed Solution', placeholder: 'How will you solve this?' },
    { key: 'current_alternatives', label: 'Current alternatives', placeholder: 'What exists today?' },
    { key: 'unique_edge', label: 'Unique edge', placeholder: 'What makes this different?' },
    { key: 'mvp_scope', label: 'MVP scope', placeholder: 'What\'s the smallest version?' },
    { key: 'go_to_market', label: 'Go-to-market', placeholder: 'How will you reach users?' },
    { key: 'monetization', label: 'Monetization', placeholder: 'How will this make money?' },
    { key: 'risks_unknowns', label: 'Risks & unknowns', placeholder: 'What could go wrong?' },
    { key: 'next_action', label: 'Next action', placeholder: 'What\'s the very next step?' },
  ],
  campaign: [
    { key: 'objective', label: 'Objective', placeholder: 'What\'s the goal?' },
    { key: 'target_audience', label: 'Target audience', placeholder: 'Who are you reaching?' },
    { key: 'key_insight', label: 'Key insight', placeholder: 'What truth drives this?' },
    { key: 'core_message', label: 'Core message', placeholder: 'What\'s the main message?' },
    { key: 'offer_hook', label: 'Offer / hook', placeholder: 'What\'s the hook?' },
    { key: 'channels', label: 'Channels', placeholder: 'Where will this run?' },
    { key: 'content_plan', label: 'Content plan', placeholder: 'What content will you create?' },
    { key: 'timeline', label: 'Timeline', placeholder: 'When does each phase happen?' },
    { key: 'budget', label: 'Budget', placeholder: 'What\'s the budget?' },
    { key: 'success_metrics', label: 'Success metrics', placeholder: 'How will you measure success?' },
    { key: 'next_action', label: 'Next action', placeholder: 'What\'s the very next step?' },
  ],
  content: [
    { key: 'topic', label: 'Topic', placeholder: 'What\'s the topic?' },
    { key: 'audience', label: 'Audience', placeholder: 'Who is this for?' },
    { key: 'angle_pov', label: 'Angle / POV', placeholder: 'What\'s your angle?' },
    { key: 'hook', label: 'Hook (first 3 seconds)', placeholder: 'What grabs attention?' },
    { key: 'outline', label: 'Outline', placeholder: 'What\'s the structure?' },
    { key: 'examples_proof', label: 'Examples / proof', placeholder: 'What evidence supports this?' },
    { key: 'cta', label: 'CTA', placeholder: 'What should the audience do?' },
    { key: 'distribution_plan', label: 'Distribution plan', placeholder: 'How will this reach people?' },
    { key: 'success_metrics', label: 'Success metrics', placeholder: 'How will you measure success?' },
    { key: 'next_action', label: 'Next action', placeholder: 'What\'s the very next step?' },
  ],
  product: [
    { key: 'user_problem', label: 'User problem', placeholder: 'What pain does the user feel?' },
    { key: 'target_user', label: 'Target user', placeholder: 'Who is the primary user?' },
    { key: 'jtbd', label: 'Jobs-to-be-done', placeholder: 'What job is the user hiring this for?' },
    { key: 'key_features', label: 'Key features', placeholder: 'What are the core features?' },
    { key: 'differentiator', label: 'Differentiator', placeholder: 'What makes this unique?' },
    { key: 'pricing_hypothesis', label: 'Pricing hypothesis', placeholder: 'How might you price this?' },
    { key: 'mvp_scope', label: 'MVP scope', placeholder: 'What\'s the smallest version?' },
    { key: 'validation_plan', label: 'Validation plan', placeholder: 'How will you test assumptions?' },
    { key: 'risks', label: 'Risks', placeholder: 'What could go wrong?' },
    { key: 'next_action', label: 'Next action', placeholder: 'What\'s the very next step?' },
  ],
};

export function getTemplate(type: TemplateType): TemplateDefinition {
  return TEMPLATES.find(t => t.key === type) || TEMPLATES[0];
}

export function getSchemaForTemplate(type: TemplateType): StructureField[] {
  return STRUCTURE_SCHEMAS[type] || STRUCTURE_SCHEMAS.base;
}

/**
 * Normalize legacy 'blank' to 'base'
 */
export function normalizeTemplateType(raw: string | null | undefined): TemplateType {
  if (!raw || raw === 'blank') return 'base';
  if (raw in STRUCTURE_SCHEMAS) return raw as TemplateType;
  return 'base';
}

/**
 * Create an empty structure object for a given template type
 */
export function createEmptyStructure(type: TemplateType): Record<string, string> {
  const schema = STRUCTURE_SCHEMAS[type] || STRUCTURE_SCHEMAS.base;
  const obj: Record<string, string> = {};
  for (const field of schema) {
    obj[field.key] = '';
  }
  return obj;
}

/**
 * Check if a structure's keys match the schema for a given template type
 */
export function structureMatchesSchema(structure: Record<string, string> | null, type: TemplateType): boolean {
  if (!structure || Object.keys(structure).length === 0) return false;
  const schema = STRUCTURE_SCHEMAS[type] || STRUCTURE_SCHEMAS.base;
  const schemaKeys = new Set(schema.map(f => f.key));
  const structKeys = Object.keys(structure);
  return structKeys.length === schemaKeys.size && structKeys.every(k => schemaKeys.has(k));
}
